import React, { useEffect, useRef, useState } from "react";
import { Box, InputBase, Tooltip, Typography } from "@mui/material";
import { round2 } from "../lib/annotations";

const EXPRESSION = /^[+-]?(\d+\.?\d*|\.\d+)([+-](\d+\.?\d*|\.\d+))*$/;

function evaluate(expression) {
  const compact = expression.replace(/\s+/g, "");
  if (!EXPRESSION.test(compact))
    return { ok: false, reason: "Use numbers like 3 or 2+1.5" };
  const tokens = compact.match(/[+-]?[^+-]+/g) || [];
  const sum = round2(tokens.reduce((total, token) => total + Number(token), 0));
  if (Number.isNaN(sum)) return { ok: false, reason: "Invalid marks" };
  if (sum < 0) return { ok: false, reason: "Marks cannot be negative" };
  return { ok: true, value: sum };
}

const toText = (value) =>
  value === null || value === undefined ? "" : String(value);

export default function MarkInput({
  max,
  value,
  work,
  disabled,
  inputRef,
  onChange,
  onFocus,
  onNext,
}) {
  const [text, setText] = useState(toText(value));
  const [error, setError] = useState("");
  const focused = useRef(false);
  const committed = useRef(toText(value));

  useEffect(() => {
    committed.current = toText(value);
    if (!focused.current) {
      setText(committed.current);
      setError("");
    }
  }, [value]);

  const commit = () => {
    const raw = text.trim().replace(/=$/, "");
    if (raw === committed.current) {
      setError("");
      return true;
    }
    if (!raw) {
      committed.current = "";
      setError("");
      onChange({ mark: null, work: "" });
      return true;
    }

    const result = evaluate(raw);
    if (!result.ok) {
      setError(result.reason);
      return false;
    }
    if (result.value > max) {
      setError(`Maximum is ${max}`);
      return false;
    }

    setError("");
    committed.current = String(result.value);
    setText(committed.current);
    onChange({
      mark: result.value,
      work: raw === committed.current ? "" : `${raw}=${result.value}`,
    });
    return true;
  };

  return (
    <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
      <Tooltip
        arrow
        placement="top"
        open={Boolean(error)}
        title={error}
        disableHoverListener
        disableFocusListener
        disableTouchListener
      >
        <Tooltip arrow placement="top" title={!error && work ? work : ""}>
          <InputBase
            inputRef={inputRef}
            value={text}
            disabled={disabled}
            placeholder="–"
            inputProps={{
              inputMode: "decimal",
              "aria-label": `Marks out of ${max}`,
              "aria-invalid": Boolean(error),
            }}
            onChange={(event) => {
              setText(event.target.value);
              if (error) setError("");
            }}
            onFocus={(event) => {
              focused.current = true;
              event.target.select();
              onFocus?.();
            }}
            onBlur={() => {
              focused.current = false;
              commit();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                if (commit()) onNext?.();
              }
              if (event.key === "Escape") {
                setText(committed.current);
                setError("");
                event.currentTarget.querySelector("input")?.blur();
              }
            }}
            sx={{
              width: 58,
              height: 32,
              borderRadius: 1.25,
              bgcolor: disabled ? "#f4f6fa" : "#fff",
              border: "1px solid",
              borderColor: error ? "error.main" : "#cdd7e6",
              "&.Mui-focused": {
                borderColor: error ? "error.main" : "primary.main",
                boxShadow: "0 0 0 3px rgba(10,108,240,.14)",
              },
              "& input": {
                p: 0,
                textAlign: "center",
                fontSize: 14,
                fontWeight: 700,
              },
            }}
          />
        </Tooltip>
      </Tooltip>
      <Typography variant="caption" color="text.secondary" sx={{ width: 26 }}>
        /{max}
      </Typography>
    </Box>
  );
}
